/**
 * The tutorial, loaded on demand. SPEC.md §17.1, §35, §44.
 *
 * Sixteen chapters, a rules engine, twelve sprites and four catalogues have no
 * business in the bundle a player downloads to type a join code into, so the
 * whole feature is behind one dynamic import. What is left in the main bundle is
 * a button and the language picker.
 *
 * **A deferred chunk has to degrade, and this one does.** CLAUDE.md is explicit
 * about the trap: a shell left running across a deploy asks for a chunk hash the
 * new precache does not have and the server no longer serves, so the import
 * rejects. `ShareCard` handles that by falling back to the join code. There is
 * no smaller version of a tutorial to fall back to, so this does the honest
 * thing instead — it says the shell is out of date and offers the reload that
 * fixes it, which is exactly what the failure means. Silence, or a spinner that
 * never resolves, would be the same event with no way out of it. §35, §44
 *
 * A manual import rather than `React.lazy`, because `lazy` throws the rejection
 * at the nearest error boundary — which in this app is the whole-screen
 * "Something went wrong" — and a stale chunk is a recoverable, expected,
 * one-tap-to-fix condition rather than a crash.
 *
 * **The two sentences below are hardcoded English, and that is not an
 * oversight.** Every translation of them lives in the chunk this component
 * exists to report the absence of, so a localised failure message is a
 * contradiction: the only moment it would be shown is the moment it cannot be
 * loaded. They are deliberately absent from the catalogues for the same reason
 * — a key nothing can ever read is a key three translators are paid for
 * nothing. The rest of the screen the learner came from is English too. §19, §35
 */

import { useEffect, useState } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { navigate, paths } from '@/router';
import type { LocaleCode } from './i18n/locales';
import type { TutorialPageProps } from './TutorialPage';

type Loaded = (props: TutorialPageProps) => React.JSX.Element;

export function TutorialRoute({ locale }: { locale: LocaleCode }) {
  const [Page, setPage] = useState<{ render: Loaded } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('./TutorialPage')
      .then((module) => {
        if (!cancelled) setPage({ render: module.TutorialPage });
      })
      .catch((error: unknown) => {
        console.error('Tutorial chunk failed to load', error);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <main className="scroll-area flex min-h-0 flex-1 flex-col p-safe">
        <div className="m-auto flex max-w-xs flex-col items-center gap-4 text-center">
          <p className="text-ink-muted">
            The tutorial didn’t load. Reload to get the latest version.
          </p>
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="min-h-tap rounded-control bg-accent px-6 font-semibold text-accent-ink"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => {
              navigate(paths.landing());
            }}
            className="min-h-tap rounded-control px-6 font-semibold text-ink-muted"
          >
            Back to start
          </button>
        </div>
      </main>
    );
  }

  if (!Page) {
    return (
      <main className="flex min-h-0 flex-1 flex-col p-safe">
        <div className="m-auto">
          <Spinner label="Loading the tutorial…" />
        </div>
      </main>
    );
  }

  return (
    <Page.render
      locale={locale}
      onExit={() => {
        navigate(paths.landing());
      }}
    />
  );
}
